/**
 * Strip/Dock Mode Toggle — compact toggle button for switching
 * between minimal strip mode and full dock mode.
 */

import { useUiStore, type PanelMode } from '@/lib/stores/ui-store';

const modeConfig: Record<PanelMode, { icon: JSX.Element; label: string; title: string }> = {
  strip: {
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M12 17.25h8.25" />
      </svg>
    ),
    label: 'Strip',
    title: 'Switch to strip mode (compact SOAP only)',
  },
  dock: {
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
      </svg>
    ),
    label: 'Dock',
    title: 'Switch to dock mode (full panels)',
  },
};

export function StripModeToggle(): JSX.Element {
  const panelMode = useUiStore((s) => s.panelMode);
  const togglePanelMode = useUiStore((s) => s.togglePanelMode);

  // Show the opposite mode as the action (click to switch TO)
  const targetMode: PanelMode = panelMode === 'strip' ? 'dock' : 'strip';
  const config = modeConfig[targetMode];

  return (
    <button
      onClick={togglePanelMode}
      className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-500 shadow-sm ring-1 ring-slate-200/40 transition-interactive duration-200 hover:border-teal-400 hover:bg-teal-50/50 hover:text-teal-700 hover:ring-teal-200/60 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
      title={config.title}
      aria-label={config.title}
    >
      {config.icon}
      <span className="font-medium">{config.label}</span>
    </button>
  );
}
